import React, { useEffect, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import Section from "../../../layout/Section";
import SubSection from "../../../layout/SubSection";
import Button from "../../../common/inputs/Button";
import InputFieldNumber from "../../../common/inputs/InputFieldNumber";
import GewichtBalken from "../components/GewichtBalken";
import { AdminScoreSettings } from "../../../../services/api/admin";
import { useAdminScoreSettings } from "../hooks/useAdminScoreSettings";
import { useToast } from "../../../../contexts/ToastContext";
import { colors } from "../../../../styles/colors";

interface WirtschaftlichkeitScoreSectionProps {
  orgId: string;
}

type FormState = Record<keyof AdminScoreSettings, string>;

const numToStr = (value: number | undefined | null): string => {
  if (value === undefined || value === null) return "";
  return String(value);
};

const toFormState = (settings: AdminScoreSettings): FormState => ({
  irr_min: numToStr(settings.irr_min),
  irr_ziel: numToStr(settings.irr_ziel),
  investitionsvolumen_ziel: numToStr(settings.investitionsvolumen_ziel),
  investitionsvolumen_sigma: numToStr(settings.investitionsvolumen_sigma),
  ek_quote_min: numToStr(settings.ek_quote_min),
  ek_quote_max: numToStr(settings.ek_quote_max),
  gewicht_irr: numToStr((settings.gewicht_irr ?? 0) * 100),
  gewicht_investitionsvolumen: numToStr(
    (settings.gewicht_investitionsvolumen ?? 0) * 100,
  ),
  gewicht_ek_quote: numToStr((settings.gewicht_ek_quote ?? 0) * 100),
});

const GEWICHT_SUMME_TOLERANZ = 0.05;

const WirtschaftlichkeitScoreSection: React.FC<
  WirtschaftlichkeitScoreSectionProps
> = ({ orgId }) => {
  const { settings, loading, save } = useAdminScoreSettings(orgId);
  const { showSuccess, showError } = useToast();
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setForm(toFormState(settings));
    }
  }, [settings]);

  if (loading || !form) {
    return null;
  }

  const setField = (field: keyof AdminScoreSettings) => (value: string) => {
    setForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const gewichtSumme =
    (parseFloat(form.gewicht_irr) || 0) +
    (parseFloat(form.gewicht_investitionsvolumen) || 0) +
    (parseFloat(form.gewicht_ek_quote) || 0);
  const gewichtSummeValide =
    Math.abs(gewichtSumme - 100) <= GEWICHT_SUMME_TOLERANZ;

  const handleSave = async () => {
    if (!gewichtSummeValide) {
      showError("Die Gewichte müssen in Summe 100 % ergeben");
      return;
    }
    setSaving(true);
    try {
      await save({
        irr_min: parseFloat(form.irr_min) || 0,
        irr_ziel: parseFloat(form.irr_ziel) || 0,
        investitionsvolumen_ziel:
          parseFloat(form.investitionsvolumen_ziel) || 0,
        investitionsvolumen_sigma:
          parseFloat(form.investitionsvolumen_sigma) || 0,
        ek_quote_min: parseFloat(form.ek_quote_min) || 0,
        ek_quote_max: parseFloat(form.ek_quote_max) || 0,
        gewicht_irr: (parseFloat(form.gewicht_irr) || 0) / 100,
        gewicht_investitionsvolumen:
          (parseFloat(form.gewicht_investitionsvolumen) || 0) / 100,
        gewicht_ek_quote: (parseFloat(form.gewicht_ek_quote) || 0) / 100,
      });
      showSuccess("Wirtschaftlichkeits-Score-Einstellungen gespeichert");
    } catch {
      showError(
        "Wirtschaftlichkeits-Score-Einstellungen konnten nicht gespeichert werden",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section
      title="Wirtschaftlichkeits-Score"
      actions={
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          Speichern
        </Button>
      }
    >
      <Box sx={{ mb: 5 }}>
        <GewichtBalken
          segments={[
            {
              key: "gewicht_irr",
              label: "IRR",
              value: parseFloat(form.gewicht_irr) || 0,
              color: colors.scoreWeighting[0],
            },
            {
              key: "gewicht_investitionsvolumen",
              label: "Investitionsvolumen",
              value: parseFloat(form.gewicht_investitionsvolumen) || 0,
              color: colors.scoreWeighting[1],
            },
            {
              key: "gewicht_ek_quote",
              label: "EK-Quote",
              value: parseFloat(form.gewicht_ek_quote) || 0,
              color: colors.scoreWeighting[2],
            },
          ]}
          onChange={(updates) => {
            setForm((prev) => {
              if (!prev) return prev;
              const next = { ...prev };
              Object.entries(updates).forEach(([key, value]) => {
                next[key as keyof AdminScoreSettings] = String(value);
              });
              return next;
            });
          }}
        />
        <Grid container spacing={3} sx={{ alignItems: "center", mt: 2 }}>
          <Grid size={{ xs: 12, md: 4 }} sx={{ minWidth: 260 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "3px",
                  backgroundColor: colors.scoreWeighting[0],
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flex: 1 }}>
                <InputFieldNumber
                  label="IRR"
                  value={form.gewicht_irr}
                  onChange={setField("gewicht_irr")}
                  suffix="%"
                />
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ minWidth: 260 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "3px",
                  backgroundColor: colors.scoreWeighting[1],
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flex: 1 }}>
                <InputFieldNumber
                  label="Investitionsvolumen"
                  value={form.gewicht_investitionsvolumen}
                  onChange={setField("gewicht_investitionsvolumen")}
                  suffix="%"
                />
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ minWidth: 260 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "3px",
                  backgroundColor: colors.scoreWeighting[2],
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flex: 1 }}>
                <InputFieldNumber
                  label="EK-Quote"
                  value={form.gewicht_ek_quote}
                  onChange={setField("gewicht_ek_quote")}
                  suffix="%"
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
        {!gewichtSummeValide && (
          <Typography
            variant="body2"
            sx={{ color: colors.status.error.main, mt: 1 }}
          >
            Die Gewichte ergeben aktuell {gewichtSumme.toFixed(1)} % – sie
            müssen in Summe 100 % ergeben.
          </Typography>
        )}
      </Box>

      <SubSection title="IRR" defaultExpanded>
        <Grid container spacing={3} sx={{ alignItems: "center" }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <InputFieldNumber
              label="Minimum"
              tooltip="IRR, ab dem der IRR-Teilscore 0 Punkte erreicht"
              value={form.irr_min}
              onChange={setField("irr_min")}
              suffix="%"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <InputFieldNumber
              label="Ziel"
              tooltip="IRR, ab dem der IRR-Teilscore 100 Punkte erreicht"
              value={form.irr_ziel}
              onChange={setField("irr_ziel")}
              suffix="%"
            />
          </Grid>
        </Grid>
      </SubSection>

      <SubSection title="Investitionsvolumen" defaultExpanded>
        <Grid container spacing={3} sx={{ alignItems: "center" }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <InputFieldNumber
              label="Ziel"
              tooltip="Bevorzugte Ticketgröße – hier erreicht der Volumen-Teilscore 100 Punkte"
              value={form.investitionsvolumen_ziel}
              onChange={setField("investitionsvolumen_ziel")}
              suffix="€"
              allowDecimals={false}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <InputFieldNumber
              label="Streuung"
              tooltip="Breite des akzeptablen Korridors um das Ziel-Investitionsvolumen"
              value={form.investitionsvolumen_sigma}
              onChange={setField("investitionsvolumen_sigma")}
              suffix="€"
              allowDecimals={false}
            />
          </Grid>
        </Grid>
      </SubSection>

      <SubSection title="EK-Quote" defaultExpanded>
        <Grid container spacing={3} sx={{ alignItems: "center" }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <InputFieldNumber
              label="Minimum"
              tooltip="EK-Quote, ab der der EK-Teilscore 100 Punkte erreicht (beste Kapitaleffizienz)"
              value={form.ek_quote_min}
              onChange={setField("ek_quote_min")}
              suffix="%"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <InputFieldNumber
              label="Maximum"
              tooltip="EK-Quote, ab der der EK-Teilscore 0 Punkte erreicht"
              value={form.ek_quote_max}
              onChange={setField("ek_quote_max")}
              suffix="%"
            />
          </Grid>
        </Grid>
      </SubSection>
    </Section>
  );
};

export default WirtschaftlichkeitScoreSection;
